import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Users,
  Building2,
  Thermometer,
  ArrowLeft,
  ShieldCheck,
  Grid3X3,
  UserCircle,
  CalendarCheck2,
  Megaphone,
  Brain,
  Flame,
  FileCode2,
  ShoppingBag,
  PackageCheck,
  ClipboardList,
  Radio,
  BarChart3,
  KeyRound,
  GraduationCap,
} from "lucide-react";

import ScadenzeAdmin from "./ScadenzeAdmin";
import UtentiRuoli from "./UtentiRuoli";
import AziendeFrighi from "./AziendeFrighi";
import DispositiviCryo from "./DispositiviCryo";
import AziendePiani from "./AziendePiani";
import PianiFunzioni from "./PianiFunzioni";
import ProfiloAzienda from "./ProfiloAzienda";
import LoginMarketingAdmin from "./LoginMarketingAdmin";
import AppBannersAdmin from "./AppBannersAdmin";
import AttrezzatureProposteAdmin from "./AttrezzatureProposteAdmin";
import NozioniNormaAIAdmin from "./NozioniNormaAIAdmin";
import FriggitriciAdmin from "./FriggitriciAdmin";
import ManualeHaccpTemplateAdmin from "./ManualeHaccpTemplateAdmin";
import AdminShop from "./AdminShop";
import HardwareOrdersAdmin from "./HardwareOrdersAdmin";
import MultiLocationAssignments from "./MultiLocationAssignments";
import AziendeStatistiche from "./AziendeStatistiche";
import DipendentiAdmin from "./DipendentiAdmin";
import FcicTokensAdmin from "./FcicTokensAdmin";
import AcademyOrdersAdmin from "./AcademyOrdersAdmin";
import ShopOrdersAdmin from "./ShopOrdersAdmin";
import OnboardingAnalytics from "./OnboardingAnalytics";

export default function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "utenti");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/api/me.php", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setMe(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const isSuper =
    !!me?.is_super ||
    me?.ruolo === "superadmin" ||
    (Array.isArray(me?.roles) && me.roles.includes("superadmin")) ||
    (Array.isArray(me?.permessi) && me.permessi.includes("ALL"));

  const canEditProfile = me?.ruolo === "admin" || me?.ruolo === "superadmin";
  const canManageFriggitrici =
    me?.ruolo === "admin" || me?.ruolo === "superadmin";
  const currentPlanCode = (
    me?.licenza?.plan_code ||
    me?.quota?.plan_code ||
    "FREE"
  ).toUpperCase();
  const showPianiFunzioniTab = isSuper || currentPlanCode !== "CUBO_PREDICTIVE";

  useEffect(() => {
    const nextTab = searchParams.get("tab");
    if (nextTab && nextTab !== tab) {
      setTab(nextTab);
    }
  }, [searchParams, tab]);

  function openTab(nextTab) {
    setTab(nextTab);
    setSearchParams({ tab: nextTab });
  }

  if (loading) {
    return <div className="p-4">Caricamento admin...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="bg-gradient-to-r from-slate-900 via-indigo-700 to-sky-500 text-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
              <ShieldCheck size={20} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide opacity-80">
                Area riservata
              </div>
              <h1 className="text-2xl font-bold leading-tight">
                Pannello di Controllo
              </h1>
              <p className="text-sm opacity-90">
                {isSuper
                  ? "Accesso completo (superadmin)"
                  : canEditProfile
                    ? "Accesso amministratore azienda"
                    : "Accesso in sola lettura"}
              </p>
            </div>
          </div>

          <div className="flex gap-2 items-center">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold">{me?.nome || me?.email}</div>
              <div className="text-xs opacity-80">
                {me?.email} • {me?.ruolo}
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              className="rounded-lg bg-white/10 hover:bg-white/20 px-3 py-1.5 text-sm flex items-center gap-1"
            >
              <ArrowLeft size={14} /> Torna alla dashboard
            </button>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="max-w-6xl mx-auto px-4 pt-4 flex gap-2 flex-wrap">
        {me?.ruolo === "admin" && me?.can_manage_billing === true && <button onClick={() => navigate("/attiva")} className={normalBtn}><ShieldCheck size={16} />Licenza e abbonamento</button>}
        {isSuper && <button onClick={() => openTab("onboarding")} className={tab === "onboarding" ? activeBtn : normalBtn}><BarChart3 size={16} />Registrazioni e A/B</button>}
        {isSuper && <button onClick={() => openTab("appBanners")} className={tab === "appBanners" ? activeBtn : normalBtn}><Megaphone size={16} /> Banner app</button>}
        <button
          onClick={() => openTab("utenti")}
          className={tab === "utenti" ? activeBtn : normalBtn}
        >
          <Users size={16} /> Utenti
        </button>

        <button
          onClick={() => openTab("dipendenti")}
          className={tab === "dipendenti" ? activeBtn : normalBtn}
        >
          <UserCircle size={16} /> Dipendenti
        </button>

        <button
          onClick={() => openTab("piani")}
          className={tab === "piani" ? activeBtn : normalBtn}
        >
          <ShieldCheck size={16} /> Piani aziende
        </button>

        <button
          onClick={() => openTab("aziende")}
          className={tab === "aziende" ? activeBtn : normalBtn}
        >
          <Building2 size={16} /> Aziende & Frighi
        </button>

        {isSuper && (
          <button
            onClick={() => openTab("shopOrders")}
            className={tab === "shopOrders" ? activeBtn : normalBtn}
          >
            <PackageCheck size={16} /> Ordini Shop
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("academyOrders")}
            className={tab === "academyOrders" ? activeBtn : normalBtn}
          >
            <GraduationCap size={16} /> Ordini Academy
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("multilocation")}
            className={tab === "multilocation" ? activeBtn : normalBtn}
          >
            <Building2 size={16} /> Multilocation
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("fcicTokens")}
            className={tab === "fcicTokens" ? activeBtn : normalBtn}
          >
            <KeyRound size={16} /> Token FCIC
          </button>
        )}

        <button
          onClick={() => openTab("cryo")}
          className={tab === "cryo" ? activeBtn : normalBtn}
        >
          <Thermometer size={16} /> Dispositivi / Cryo
        </button>

        {canManageFriggitrici && (
          <button
            onClick={() => openTab("friggitrici")}
            className={tab === "friggitrici" ? activeBtn : normalBtn}
          >
            <Flame size={16} /> Friggitrici
          </button>
        )}

        {showPianiFunzioniTab && (
          <button
            onClick={() => openTab("pianiFunzioni")}
            className={tab === "pianiFunzioni" ? activeBtn : normalBtn}
          >
            <Grid3X3 size={16} /> Funzioni / Piani
          </button>
        )}

        <button
          onClick={() => openTab("scadenze")}
          className={tab === "scadenze" ? activeBtn : normalBtn}
        >
          <CalendarCheck2 size={16} /> Attività & Scadenze
        </button>

        {isSuper && (
          <button
            onClick={() => openTab("attrezzatureProposte")}
            className={tab === "attrezzatureProposte" ? activeBtn : normalBtn}
          >
            <Thermometer size={16} /> Richieste attrezzature
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("nozioniNormaAI")}
            className={tab === "nozioniNormaAI" ? activeBtn : normalBtn}
          >
            <Brain size={16} /> Nozioni Norma AI
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("manualeTemplate")}
            className={tab === "manualeTemplate" ? activeBtn : normalBtn}
          >
            <FileCode2 size={16} /> Template Manuale HACCP
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("shop")}
            className={tab === "shop" ? activeBtn : normalBtn}
          >
            <ShoppingBag size={16} /> Shop
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("aziendeStatistiche")}
            className={tab === "aziendeStatistiche" ? activeBtn : normalBtn}
          >
            <BarChart3 size={16} /> Statistiche aziende
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => navigate("/admin/lorawan-devices")}
            className={normalBtn}
          >
            <Radio size={16} /> LoRaWAN
          </button>
        )}

        {isSuper && (
          <button
            onClick={() => openTab("hardwareOrders")}
            className={tab === "hardwareOrders" ? activeBtn : normalBtn}
          >
            <ClipboardList size={16} /> Ordini hardware
          </button>
        )}

        <button
          onClick={() => openTab("profilo")}
          className={tab === "profilo" ? activeBtn : normalBtn}
        >
          <UserCircle size={16} /> Profilo
        </button>

        {isSuper && (
          <button
            onClick={() => openTab("loginMarketing")}
            className={tab === "loginMarketing" ? activeBtn : normalBtn}
          >
            <Megaphone size={16} /> Login Marketing
          </button>
        )}
      </div>

      {/* CONTENUTO */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        {tab === "onboarding" && isSuper && <OnboardingAnalytics />}
        {tab === "appBanners" && isSuper && <AppBannersAdmin />}
        {tab === "utenti" && <UtentiRuoli isSuper={isSuper} />}
        {tab === "dipendenti" && <DipendentiAdmin isSuper={isSuper} />}
        {tab === "piani" && <AziendePiani isSuper={isSuper} />}
        {tab === "aziende" && <AziendeFrighi isSuper={isSuper} />}
        {tab === "multilocation" && isSuper && <MultiLocationAssignments />}
        {tab === "fcicTokens" && isSuper && (
          <FcicTokensAdmin isSuper={isSuper} />
        )}
        {tab === "cryo" && <DispositiviCryo isSuper={isSuper} />}

        {tab === "friggitrici" && canManageFriggitrici && (
          <FriggitriciAdmin me={me} isSuper={isSuper} />
        )}

        {tab === "pianiFunzioni" && showPianiFunzioniTab && (
          <PianiFunzioni isSuper={isSuper} planCode={currentPlanCode} />
        )}
        {tab === "scadenze" && <ScadenzeAdmin me={me} isSuper={isSuper} />}
        {tab === "aziendeStatistiche" && isSuper && (
          <AziendeStatistiche isSuper={isSuper} />
        )}

        {tab === "attrezzatureProposte" && isSuper && <AttrezzatureProposteAdmin />}
        {tab === "nozioniNormaAI" && isSuper && <NozioniNormaAIAdmin />}

        {tab === "manualeTemplate" && isSuper && (
          <ManualeHaccpTemplateAdmin isSuper={isSuper} />
        )}

        {tab === "shop" && isSuper && <AdminShop />}

        {tab === "hardwareOrders" && isSuper && (
          <HardwareOrdersAdmin isSuper={isSuper} />
        )}

        {tab === "academyOrders" && isSuper && <AcademyOrdersAdmin />}
        {tab === "shopOrders" && isSuper && <ShopOrdersAdmin />}

        {tab === "profilo" && (
          <ProfiloAzienda me={me} canEdit={canEditProfile} isSuper={isSuper} />
        )}

        {tab === "loginMarketing" && isSuper && (
          <LoginMarketingAdmin isSuper={isSuper} />
        )}
      </div>
    </div>
  );
}

/* STILI BOTTONI */
const activeBtn =
  "px-4 py-2 rounded-full bg-slate-900 text-white shadow-sm flex items-center gap-2";

const normalBtn =
  "px-4 py-2 rounded-full bg-white text-slate-700 hover:bg-slate-100 shadow-sm flex items-center gap-2";
